import { useRef, useState } from "react";
import { Upload, ClipboardPaste, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PillRail, Pill } from "@/components/common/Segmented";
import { btn } from "@/components/common/btn";
import { money } from "@/lib/format";
import { importProposalLines } from "@/lib/api";

const cell = "h-7 w-full rounded-md border border-slate-200 bg-background px-1.5 text-xs outline-none focus:border-brand";

// Paste rows from Excel, or upload an .xlsx / .csv / vendor PDF quote → preview → add to the scope.
export function ImportLinesDialog({ open, onClose, scopeName, masterParts, onAdd }) {
  const [mode, setMode] = useState("paste");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const fileRef = useRef(null);

  const reset = () => { setText(""); setResult(null); setRows([]); setBusy(false); };
  const close = () => { reset(); onClose(); };
  const libMatch = (pn) => pn && masterParts.find((p) => p.part_number && p.part_number.toLowerCase() === pn.toLowerCase());

  const run = async (fd) => {
    setBusy(true);
    try {
      const { data } = await importProposalLines(fd);
      if (!(data.items || []).length) { toast.error(data.warnings?.[0] || "No line items found"); return; }
      setResult(data);
      setRows((data.items || []).map((it, i) => ({ ...it, _id: i, _on: true })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse that quote");
    } finally { setBusy(false); }
  };
  const parsePaste = () => { const fd = new FormData(); fd.append("text", text); run(fd); };
  const parseFile = (f) => { if (!f) return; const fd = new FormData(); fd.append("file", f); run(fd); };
  const patch = (id, k, v) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, [k]: v } : r)));
  const chosen = rows.filter((r) => r._on && (r.name || r.part_number));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-4xl" data-testid="import-lines-dialog">
        <DialogHeader><DialogTitle className="text-base">Import Vendor Quote{scopeName ? ` → ${scopeName}` : ""}</DialogTitle></DialogHeader>
        {!result && (
          <div className="space-y-3">
            <PillRail>
              <Pill on={mode === "paste"} onClick={() => setMode("paste")} testId="import-mode-paste">Paste Rows</Pill>
              <Pill on={mode === "upload"} onClick={() => setMode("upload")} testId="import-mode-upload">Upload File</Pill>
            </PillRail>
            {mode === "paste" ? (
              <>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={"Copy rows from Excel and paste here — e.g.\nQty\tPart #\tDescription\tUnit Price\n4\tUKT-001\tHinge kit\t125.00"} className="font-mono text-xs" data-testid="import-paste-text" />
                <div className="flex justify-end"><button type="button" className={btn("data")} disabled={busy || !text.trim()} onClick={parsePaste} data-testid="import-parse-btn">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardPaste className="h-3.5 w-3.5" />} Parse Rows</button></div>
              </>
            ) : (
              <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-10 text-sm text-slate-500 hover:border-brand hover:text-slate-700" data-testid="import-dropzone">
                {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
                <span>{busy ? "Reading quote…" : "Choose an Excel, CSV or PDF vendor quote"}</span>
                <span className="text-xs text-slate-400">Tables are read directly; free-form PDFs are parsed by AI.</span>
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.tsv,.txt,.pdf" className="hidden" onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="import-file-input" />
              </button>
            )}
          </div>
        )}
        {result && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span data-testid="import-result-summary">{rows.length} line{rows.length === 1 ? "" : "s"} found{result.vendor ? ` · ${result.vendor}` : ""}{result.quote_number ? ` · Quote ${result.quote_number}` : ""} · {result.source === "ai" ? "AI-parsed — check the numbers" : "read from table"}</span>
              <button type="button" className={`${btn("nav")} ml-auto`} onClick={reset} data-testid="import-start-over">Start Over</button>
            </div>
            <div className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[11px] text-slate-500">
                  <th className="w-8 px-2 py-1.5"></th><th className="w-16 px-1 py-1.5">Qty</th><th className="w-32 px-1 py-1.5">Part #</th><th className="px-1 py-1.5">Name</th><th className="w-28 px-1 py-1.5 text-right">Unit Cost</th><th className="w-24 px-1 py-1.5 text-right">Ext.</th><th className="w-24 px-2 py-1.5">Library</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const m = libMatch(r.part_number);
                    return (
                      <tr key={r._id} className={`border-t border-slate-200 ${r._on ? "" : "opacity-50"}`} data-testid={`import-row-${r._id}`}>
                        <td className="px-2"><Checkbox checked={r._on} onCheckedChange={(v) => patch(r._id, "_on", !!v)} data-testid={`import-row-on-${r._id}`} /></td>
                        <td className="px-1 py-1"><input type="number" min="1" value={r.qty} onChange={(e) => patch(r._id, "qty", parseInt(e.target.value) || 1)} className={`${cell} text-right`} data-testid={`import-row-qty-${r._id}`} /></td>
                        <td className="px-1 py-1"><input value={r.part_number} onChange={(e) => patch(r._id, "part_number", e.target.value)} className={`${cell} font-mono`} data-testid={`import-row-part-${r._id}`} /></td>
                        <td className="px-1 py-1"><input value={r.name} onChange={(e) => patch(r._id, "name", e.target.value)} className={cell} data-testid={`import-row-name-${r._id}`} /></td>
                        <td className="px-1 py-1"><input type="number" step="0.01" value={r.unit_cost} onChange={(e) => patch(r._id, "unit_cost", parseFloat(e.target.value) || 0)} className={`${cell} text-right`} data-testid={`import-row-cost-${r._id}`} /></td>
                        <td className="px-1 text-right tabular-nums text-slate-500">{money((r.qty || 1) * (r.unit_cost || 0))}</td>
                        <td className="px-2">{m ? <span className="rounded bg-emerald-100 px-1.5 py-px text-[10px] font-semibold text-emerald-700" title={`Labor + margins from library part ${m.name}`}>matched</span> : <span className="text-[10px] text-slate-400">new</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Matched parts take hours and margins from the library; the quote's unit cost is used for everyone.</span>
              <button type="button" className={btn("write")} disabled={!chosen.length} onClick={() => { onAdd(chosen, result); close(); }} data-testid="import-add-btn">Add {chosen.length} Line{chosen.length === 1 ? "" : "s"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
